import type { Tenant, TenantProjectHealthOption, TenantProjectStatusOption } from '~/types/models';

export const DEFAULT_PROJECT_STATUSES: TenantProjectStatusOption[] = [
    { value: 'Planning', label: 'Planning', color: '#0ea5e9' },
    { value: 'Active', label: 'Active', color: '#7c3aed' },
    { value: 'On Hold', label: 'On Hold', color: '#f59e0b' },
    { value: 'Completed', label: 'Completed', color: '#16a34a' },
    { value: 'Archived', label: 'Archived', color: '#64748b' },
];

export const DEFAULT_PROJECT_HEALTH_OPTIONS: TenantProjectHealthOption[] = [
    { value: 'healthy', label: 'Healthy', color: '#16a34a' },
    { value: 'in need', label: 'In Need', color: '#f59e0b' },
    { value: 'sick', label: 'Sick', color: '#dc2626' },
];

function normalizeColor(color: unknown, fallback: string) {
    const value = String(color || '').trim();
    return value || fallback;
}

function normalizeProjectOptions<T extends { value: string; label: string; color?: string }>(
    options: T[] | undefined,
    fallback: T[],
): T[] {
    if (!Array.isArray(options) || options.length === 0) {
        return fallback;
    }

    const seen = new Set<string>();

    return options
        .map((option, index) => {
            const value = String(option.value || option.label || '').trim();
            const label = String(option.label || option.value || '').trim();
            const fallbackOption = fallback[index] || fallback[0];

            if (!value || !label || seen.has(value.toLowerCase())) {
                return null;
            }

            seen.add(value.toLowerCase());

            return {
                value,
                label,
                color: normalizeColor(option.color, String(fallbackOption?.color || '#64748b')),
            } as T;
        })
        .filter((option): option is T => option !== null);
}

export function getTenantProjectStatuses(tenant?: Tenant | null): TenantProjectStatusOption[] {
    return normalizeProjectOptions(tenant?.projectSettings?.statuses, DEFAULT_PROJECT_STATUSES);
}

export function getTenantProjectHealthOptions(tenant?: Tenant | null): TenantProjectHealthOption[] {
    return normalizeProjectOptions(tenant?.projectSettings?.healthOptions, DEFAULT_PROJECT_HEALTH_OPTIONS);
}

export function getProjectStatusOption(value: string | undefined, options: TenantProjectStatusOption[]) {
    const normalized = String(value || '').trim().toLowerCase();
    return options.find(option => (
        option.value.trim().toLowerCase() === normalized
        || option.label.trim().toLowerCase() === normalized
    )) || null;
}

export function getProjectHealthOption(value: string | undefined, options: TenantProjectHealthOption[]) {
    const normalized = String(value || '').trim().toLowerCase();
    return options.find(option => option.value.trim().toLowerCase() === normalized) || null;
}
